import { gsap } from 'gsap';
import Swiper from 'swiper';

const MOBILE_QUERY = '(max-width: 991px)';
const FIT_INSET = 0.94; // Default view leaves a little room around the map
const MAX_ZOOM = 8; // Relative to the default view
const ACTIVE_ZOOM = 4; // Zoom when a location is selected
const ZOOM_STEP = 1.6;
const DRAG_THRESHOLD = 5; // px moved before a press becomes a pan (so pins/cards still click)
const OFF_FOCUS_DISTANCE = 0.15; // Share of the viewport the pin can move before "Recenter"
const PIN_GROWTH = 0.65; // Pins grow with zoom^PIN_GROWTH, slower than the map itself

export default function LocationsListing(el, {
    isActiveClass,
    listTrackHandle,
    listItemsHandle,
    listIntroHandle,
    cardHandle,
    viewportHandle,
    stageHandle,
    pinHandle,
    recenterHandle,
    zoomInHandle,
    zoomOutHandle,
    toggleHandle,
}) {
    const track = el.querySelector(listTrackHandle);
    const items = el.querySelector(listItemsHandle);
    const intro = el.querySelector(listIntroHandle);
    const cards = Array.from(el.querySelectorAll(cardHandle));
    const viewport = el.querySelector(viewportHandle);
    const stage = el.querySelector(stageHandle);
    const pins = Array.from(el.querySelectorAll(pinHandle));
    const recenter = el.querySelector(recenterHandle);
    const zoomIn = el.querySelector(zoomInHandle);
    const zoomOut = el.querySelector(zoomOutHandle);
    const toggles = Array.from(el.querySelectorAll(`${toggleHandle} button`));
    const mobile = window.matchMedia(MOBILE_QUERY);

    const view = { scale: 1, x: 0, y: 0 };
    let tween = null;
    let activeId = null;
    const pressedToggle = toggles.find(t => t.getAttribute('aria-pressed') === 'true');
    let activeType = pressedToggle ? pressedToggle.dataset.type : null;
    let swiper = null;
    let syncingSwiper = false;

    const cardFor = id => cards.find(c => c.dataset.locationId === id);
    const pinFor = id => pins.find(p => p.dataset.locationId === id);

    /**
     * View geometry
     * The stage holds the map image and pins at the map's aspect ratio; it's transformed
     * (translate + scale, origin top left) inside the viewport to pan and zoom.
     */
    function minScale() {
        const fit = Math.min(1, viewport.clientHeight / stage.offsetHeight);
        return fit * FIT_INSET;
    }

    function clamp(target) {
        const scale = Math.min(Math.max(target.scale, minScale()), minScale() * MAX_ZOOM);
        const w = stage.offsetWidth * scale;
        const h = stage.offsetHeight * scale;
        const vw = viewport.clientWidth;
        const vh = viewport.clientHeight;

        // Center an axis that fits; otherwise keep the map edge-to-edge
        const x = w <= vw ? (vw - w) / 2 : Math.min(0, Math.max(vw - w, target.x));
        const y = h <= vh ? (vh - h) / 2 : Math.min(0, Math.max(vh - h, target.y));

        return { scale, x, y };
    }

    function defaultView() {
        return clamp({ scale: minScale(), x: 0, y: 0 });
    }

    function pinPoint(pin) {
        return {
            x: (parseFloat(pin.style.left) / 100) * stage.offsetWidth,
            y: (parseFloat(pin.style.top) / 100) * stage.offsetHeight,
        };
    }

    function viewCenteredOn(pin, scale) {
        const p = pinPoint(pin);

        return clamp({
            scale,
            x: viewport.clientWidth / 2 - p.x * scale,
            y: viewport.clientHeight / 2 - p.y * scale,
        });
    }

    /**
     * Whether the active pin has been panned away from where selecting it puts it
     * (the viewport center, or as close as the map's edges allow)
     */
    function isPinOffFocus(pin) {
        const p = pinPoint(pin);
        const focused = viewCenteredOn(pin, view.scale);
        const dx = view.x - focused.x;
        const dy = view.y - focused.y;
        const limit = Math.min(viewport.clientWidth, viewport.clientHeight) * OFF_FOCUS_DISTANCE;
        const x = view.x + p.x * view.scale;
        const y = view.y + p.y * view.scale;
        const outOfView = x < 0 || x > viewport.clientWidth || y < 0 || y > viewport.clientHeight;

        return outOfView || Math.hypot(dx, dy) > limit;
    }

    function updateRecenter() {
        const pin = activeId ? pinFor(activeId) : null;
        recenter.hidden = !pin || pin.hidden || !isPinOffFocus(pin);
    }

    function render() {
        stage.style.transform = `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;
        // Pins grow as the map zooms, but more slowly than the map
        stage.style.setProperty('--pin-scale', (view.scale / minScale()) ** (PIN_GROWTH - 1));
        // Let the page scroll over the map until it's zoomed in
        viewport.style.touchAction = view.scale > minScale() * 1.01 ? 'none' : 'pan-y';
        zoomOut.disabled = view.scale <= minScale() * 1.01;
        zoomIn.disabled = view.scale >= minScale() * MAX_ZOOM * 0.99;
    }

    function setView(target, { animate = true, user = false } = {}) {
        if (tween) {
            tween.kill();
            tween = null;
        }

        const next = clamp(target);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!animate || reduceMotion) {
            Object.assign(view, next);
            render();
            if (user) {
                updateRecenter();
            }
            return;
        }

        tween = gsap.to(view, {
            ...next,
            duration: 0.6,
            ease: 'power2.inOut',
            onUpdate: render,
            onComplete: () => {
                tween = null;
                updateRecenter();
            },
        });
    }

    function zoomAround(point, factor, options) {
        const scale = Math.min(Math.max(view.scale * factor, minScale()), minScale() * MAX_ZOOM);
        const ratio = scale / view.scale;

        setView({
            scale,
            x: point.x - (point.x - view.x) * ratio,
            y: point.y - (point.y - view.y) * ratio,
        }, options);
    }

    function viewportCenter() {
        return { x: viewport.clientWidth / 2, y: viewport.clientHeight / 2 };
    }

    /**
     * Selection
     */
    function revealCard(card) {
        const item = card.closest('li');

        if (swiper) {
            const index = swiper.slides.indexOf(item);
            if (index > -1 && index !== swiper.activeIndex) {
                syncingSwiper = true;
                swiper.slideTo(index);
                syncingSwiper = false;
            }
            return;
        }

        // Scroll only the sidebar, never the page
        const top = item.offsetTop - (track.clientHeight - item.offsetHeight) / 2;
        track.scrollTo({ top, behavior: 'smooth' });
    }

    function activate(id, { reveal = false } = {}) {
        activeId = id;

        cards.forEach(c => {
            const isActive = c.dataset.locationId === id;
            c.classList.toggle(isActiveClass, isActive);
            c.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        pins.forEach(p => {
            const isActive = p.dataset.locationId === id;
            p.classList.toggle(isActiveClass, isActive);
            p.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        const card = cardFor(id);
        if (reveal && card) {
            revealCard(card);
        }

        const pin = pinFor(id);
        if (pin) {
            recenter.hidden = true;
            setView(viewCenteredOn(pin, Math.max(view.scale, minScale() * ACTIVE_ZOOM)));
        }
    }

    function deactivate() {
        activeId = null;
        [...cards, ...pins].forEach(n => {
            n.classList.remove(isActiveClass);
            n.setAttribute('aria-pressed', 'false');
        });
        recenter.hidden = true;
    }

    /**
     * Mobile carousel
     * The intro card ("Nationwide Locations") is first, so nothing looks selected on load;
     * swiping to a card selects it, and swiping back to the intro resets the map.
     */
    function initSwiper() {
        if (swiper || !mobile.matches) {
            return;
        }

        track.classList.add('swiper');
        items.classList.add('swiper-wrapper');
        Array.from(items.children).forEach(li => li.classList.toggle('swiper-slide', !li.hidden));

        swiper = new Swiper(track, {
            slidesPerView: 'auto',
            centeredSlides: true,
            centeredSlidesBounds: true, // The intro card sits at the start, not centered
            spaceBetween: 12,
            slidesOffsetBefore: 24,
            slidesOffsetAfter: 24,
            speed: 400,
            slideToClickedSlide: true,
        });

        swiper.on('slideChange', () => {
            if (syncingSwiper) {
                return;
            }

            const slide = swiper.slides[swiper.activeIndex];
            if (slide === intro) {
                deactivate();
                setView(defaultView());
                return;
            }

            const card = slide.querySelector(cardHandle);
            if (card && card.dataset.locationId !== activeId) {
                activate(card.dataset.locationId);
            }
        });

        const activeCard = activeId ? cardFor(activeId) : null;
        if (activeCard) {
            revealCard(activeCard);
        }
    }

    function destroySwiper() {
        if (!swiper) {
            return;
        }

        swiper.destroy(true, true);
        swiper = null;
        track.classList.remove('swiper');
        items.classList.remove('swiper-wrapper');
        Array.from(items.children).forEach(li => li.classList.remove('swiper-slide'));
    }

    /**
     * Location type toggle
     */
    function setType(type) {
        if (type === activeType) {
            return;
        }

        activeType = type;
        toggles.forEach(t => t.setAttribute('aria-pressed', t.dataset.type === type ? 'true' : 'false'));
        cards.forEach(c => { c.closest('li').hidden = c.closest('li').dataset.type !== type; });
        pins.forEach(p => { p.hidden = p.dataset.type !== type; });

        deactivate();
        setView(defaultView());
        track.scrollTop = 0;

        destroySwiper();
        initSwiper();
    }

    /**
     * Pointer panning and pinch zooming
     * No capture, so a press that doesn't move still clicks the pin underneath.
     */
    const pointers = new Map();
    let gesture = null;
    let suppressClick = false;

    function gestureState() {
        const points = Array.from(pointers.values());
        const rect = viewport.getBoundingClientRect();
        const mid = {
            x: points.reduce((sum, p) => sum + p.x, 0) / points.length - rect.left,
            y: points.reduce((sum, p) => sum + p.y, 0) / points.length - rect.top,
        };
        const distance = points.length > 1
            ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
            : 0;

        return { mid, distance, count: points.length };
    }

    function onPointerMove(e) {
        if (!pointers.has(e.pointerId)) {
            return;
        }
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        const now = gestureState();
        const dx = now.mid.x - gesture.mid.x;
        const dy = now.mid.y - gesture.mid.y;

        if (!gesture.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD && now.count < 2) {
            return;
        }

        gesture.moved = true;
        el.classList.add('is-panning');

        let next = { scale: view.scale, x: view.x + dx, y: view.y + dy };
        if (now.count > 1 && gesture.distance > 0 && now.count === gesture.count) {
            const scale = Math.min(
                Math.max(view.scale * (now.distance / gesture.distance), minScale()),
                minScale() * MAX_ZOOM,
            );
            const ratio = scale / view.scale;
            next = {
                scale,
                x: now.mid.x - (now.mid.x - next.x) * ratio,
                y: now.mid.y - (now.mid.y - next.y) * ratio,
            };
        }

        setView(next, { animate: false, user: true });
        gesture = { ...now, moved: true };
    }

    function onPointerUp(e) {
        pointers.delete(e.pointerId);

        if (pointers.size) {
            gesture = { ...gestureState(), moved: gesture.moved };
            return;
        }

        if (gesture && gesture.moved) {
            suppressClick = true;
            setTimeout(() => { suppressClick = false; }, 0);
        }
        gesture = null;
        el.classList.remove('is-panning');
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
    }

    viewport.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }

        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        gesture = { ...gestureState(), moved: gesture ? gesture.moved : false };

        if (pointers.size === 1) {
            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', onPointerUp);
            window.addEventListener('pointercancel', onPointerUp);
        }
    });

    // A pan that ends over a pin shouldn't select it
    viewport.addEventListener('click', e => {
        if (suppressClick) {
            e.stopPropagation();
            e.preventDefault();
        }
    }, true);

    /**
     * Events
     */
    cards.forEach(card => {
        card.addEventListener('click', () => {
            activate(card.dataset.locationId, { reveal: Boolean(swiper) });
        });
    });

    pins.forEach(pin => {
        pin.addEventListener('click', () => {
            activate(pin.dataset.locationId, { reveal: true });
        });
    });

    toggles.forEach(toggle => {
        toggle.addEventListener('click', () => setType(toggle.dataset.type));
    });

    zoomIn.addEventListener('click', () => zoomAround(viewportCenter(), ZOOM_STEP, { user: true }));
    zoomOut.addEventListener('click', () => zoomAround(viewportCenter(), 1 / ZOOM_STEP, { user: true }));

    recenter.addEventListener('click', () => {
        const pin = activeId ? pinFor(activeId) : null;
        if (pin) {
            recenter.hidden = true;
            setView(viewCenteredOn(pin, view.scale));
        }
    });

    el.addEventListener('keydown', e => {
        if (e.key === 'Escape' && activeId) {
            deactivate();
            setView(defaultView());
        }
    });

    mobile.addEventListener('change', () => {
        if (mobile.matches) {
            initSwiper();
        } else {
            destroySwiper();
        }
    });

    // Keep the view valid when the viewport resizes (breakpoints, rotation)
    let lastWidth = viewport.clientWidth;
    new ResizeObserver(() => { // eslint-disable-line no-new
        if (viewport.clientWidth === lastWidth) {
            return;
        }
        lastWidth = viewport.clientWidth;

        const pin = activeId ? pinFor(activeId) : null;
        const target = pin
            ? viewCenteredOn(pin, Math.max(view.scale, minScale() * ACTIVE_ZOOM))
            : defaultView();
        setView(target, { animate: false });
        updateRecenter();
    }).observe(viewport);

    setView(defaultView(), { animate: false });
    initSwiper();
}
